// Utils
import globalLogger from '../../logger';
import { getNewTransactionId } from '../../utils/undoRedo/undoRedoTransactionManager';
import { getListChildNewLocation } from '../../../common/elements/utils/elementLocationUtils';
import { getCurrentlyEditingId } from '../selection/currentlyEditingSelector';
import { getElement } from '../../../common/elements/utils/elementTraversalUtils';
import { getCaption, getColor, getShowCaption, getWidth } from '../../../common/elements/utils/elementPropertyUtils';
import convertSimpleStringToTiptapContent from '../../../common/tiptap/utils/createJsonContentUtils/convertSimpleStringToTiptapContent';
import { createInitialSingleEditorTaskListContent } from '../../../common/taskLists/singleEditorTaskListContentBuilder';

// Selectors
import { getElements } from '../selectors/elementsSelector';
import { selectIsSingleTaskListEditorEnabledForCurrentUser } from '../taskList/taskListSelector';

// Actions
import { createAndEditElement } from '../actions/elementActions';
import { setLinkElementUrl } from '../link/linkActions';
import { setElementTypeAndUpdateElement } from '../../../common/elements/elementActions';
import { setSelectedElements, startEditingMainEditor } from '../selection/selectionActions';
import { setElementLocalData } from '../local/elementLocalDataActions';

// Constants
import { ElementType } from '../../../common/elements/elementTypes';
import {
    LinkConfig,
    SingleEditorTaskListConfig,
    SwatchConfig,
    TaskConfig,
    UpdateElementTypeConfig,
} from '../../../common/tiptap/extensions/universalCard/universalCardTypes';
import { INITIAL_SWATCH_PROPERTIES } from '../../../common/tiptap/extensions/universalCard/swatch';
import { TiptapContent } from '../../../common/tiptap/tiptapTypes';

const logger = globalLogger.createChannel('universalCardActions');

const updateElementTypeToLink =
    ({ id, elementType, data }: LinkConfig) =>
    (dispatch: Function) => {
        const transactionId = getNewTransactionId();

        const { url } = data;

        const rawState = convertSimpleStringToTiptapContent(url);

        const undoChanges = { textContent: rawState, caption: null, showCaption: false, ignoreAutoType: true };

        dispatch(setElementTypeAndUpdateElement({ id, elementType, undoChanges, transactionId, changes: undefined }));
        dispatch(setElementLocalData({ id, data: { url } }));
        dispatch(setLinkElementUrl({ id, url, transactionId }));
    };

/**
 * Ensure the tip tap content is created respecting the checked status.
 */
const createSingleTaskListEditorContent = ({ isComplete }: { isComplete: boolean }) => {
    const content = createInitialSingleEditorTaskListContent();
    const taskItem = (content.textContent as TiptapContent).content?.[0]?.content?.[0];

    if (taskItem?.attrs) {
        taskItem.attrs.checked = isComplete;
    }

    return content;
};

const updateElementTypeToTask =
    ({ id, data }: TaskConfig) =>
    (dispatch: Function, getState: Function) => {
        const transactionId = getNewTransactionId();
        const state = getState();
        const { isComplete } = data.content;

        if (selectIsSingleTaskListEditorEnabledForCurrentUser(state)) {
            const singleTaskListEditorContent = createSingleTaskListEditorContent({ isComplete });
            dispatch(
                setElementTypeAndUpdateElement({
                    id,
                    elementType: ElementType.TASK_LIST_TYPE,
                    transactionId,
                    changes: singleTaskListEditorContent,
                    undoChanges: undefined,
                }),
            );
            dispatch(startEditingMainEditor(id));
            return;
        }

        dispatch(
            setElementTypeAndUpdateElement({
                id,
                elementType: ElementType.TASK_LIST_TYPE,
                transactionId,
                changes: undefined,
                undoChanges: undefined,
            }),
        );
        dispatch(
            createAndEditElement({
                elementType: ElementType.TASK_TYPE,
                location: getListChildNewLocation({ listId: id }),
                content: data.content,
                transactionId,
                select: false,
            }),
        );
        dispatch(setSelectedElements({ ids: [id], rangeAnchors: null, transactionId }));
    };

/**
 * Converts a card into a single-editor task list whose content has already been built - a paste of
 * copied task items into an empty card.
 *
 * Editing moves to the new list's own editor, matching the `[] ` conversion, so the caret lands in
 * the list rather than being left on a card that no longer exists.
 */
const updateElementTypeToSingleEditorTaskList =
    ({ id, data }: SingleEditorTaskListConfig) =>
    (dispatch: Function) => {
        const transactionId = getNewTransactionId();

        dispatch(
            setElementTypeAndUpdateElement({
                id,
                elementType: ElementType.TASK_LIST_TYPE,
                transactionId,
                changes: data,
                undoChanges: undefined,
            }),
        );

        dispatch(startEditingMainEditor(id));
    };

const updateElementTypeToSwatch =
    ({ id, elementType, data }: SwatchConfig) =>
    (dispatch: Function, getState: Function) => {
        const { originalText } = data;

        const transactionId = getNewTransactionId();
        const state = getState();

        const elements = getElements(state);
        const currentElement = getElement(elements, id);

        const rawState = convertSimpleStringToTiptapContent(originalText);

        const undoChanges = {
            textContent: rawState,
            caption: getCaption(currentElement),
            showCaption: getShowCaption(currentElement),
            ignoreAutoType: true,
            color: getColor(currentElement),
            media: null,
            width: getWidth(currentElement),
            showColorValue: null,
        };

        return dispatch(
            setElementTypeAndUpdateElement({
                id,
                elementType,
                changes: {
                    ...INITIAL_SWATCH_PROPERTIES,
                    ...data,
                },
                undoChanges,
                transactionId,
            }),
        );
    };

export const updateElementType = (elementConfig: UpdateElementTypeConfig) => (dispatch: Function) => {
    switch (elementConfig.elementType) {
        case ElementType.LINK_TYPE:
            return dispatch(updateElementTypeToLink(elementConfig as LinkConfig));
        case ElementType.TASK_TYPE:
            return dispatch(updateElementTypeToTask(elementConfig as TaskConfig));
        case ElementType.TASK_LIST_TYPE:
            return dispatch(updateElementTypeToSingleEditorTaskList(elementConfig as SingleEditorTaskListConfig));
        case ElementType.COLOR_SWATCH_TYPE:
            return dispatch(updateElementTypeToSwatch(elementConfig as SwatchConfig));
        default:
            return dispatch(
                setElementTypeAndUpdateElement({
                    id: elementConfig.id,
                    elementType: elementConfig.elementType,
                    changes: elementConfig.data,
                    undoChanges: undefined,
                    transactionId: undefined,
                }),
            );
    }
};

/**
 * Performs an elementType change on the element that's currently being edited.
 */
export const updateCurrentlyEditingElementType =
    (config: Omit<UpdateElementTypeConfig, 'id'>) => (dispatch: Function, getState: Function) => {
        const state = getState();
        const currentlyEditingId = getCurrentlyEditingId(state) as string;

        if (!currentlyEditingId) {
            logger.warn('No currently editing element found');
            return;
        }

        // @ts-ignore Errors relating to config types not matching...
        return dispatch(updateElementType({ id: currentlyEditingId, ...config }));
    };
